import { Location } from "@angular/common";
import { HttpResponse, HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { fadeInRightAnimation } from "src/@fury/animations/fade-in-right.animation";
import { fadeInUpAnimation } from "src/@fury/animations/fade-in-up.animation";
import { scaleInAnimation } from "src/@fury/animations/scale-in.animation";
import { ErrorType } from "src/app/enums/error-type.enum";
import { ApiService } from "src/app/services/api.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import endpoints from "src/app/helpers/endpoints";

@Component({
  selector: "fury-add-template",
  templateUrl: "./add-template.component.html",
  styleUrls: ["./add-template.component.scss"],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation],
})
export class AddTemplateComponent implements OnInit {
  templateForm!: FormGroup;
  templateId: string;
  actionList: any[] = [];
  statusList: any[] = [];
  ngUnsubscribe = new Subject<void>();
  showTemplateEditor = false;
  editorConfig = {
    allowedContent: false,
    toolbarGroups: [
      { name: "clipboard", groups: ["clipboard", "undo"] },
      { name: "editing", groups: ["find", "selection", "spellchecker"] },
      { name: "links" },
      { name: "insert" },
      { name: "document", groups: ["mode", "document", "doctools"] },
      { name: "basicstyles", groups: ["basicstyles", "cleanup"] },
      { name: "paragraph", groups: ["list", "indent", "blocks", "align"] },
      { name: "styles" },
      { name: "colors" },
    ],
    forcePasteAsPlainText: false,
    image_previewText: " ",
  };

  constructor(
    public apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    public location: Location,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.getAllEmailTemplateAction();
    this.formInit();

    this.templateId = this.activatedRoute.snapshot.params["id"];
    if (this.templateId) {
      this.getEmailTemplate();
    }
  }

  formInit() {
    this.templateForm = new FormGroup({
      action: new FormControl("", [Validators.required]),
      status: new FormControl(""),
      templateContent: new FormControl("", [Validators.required]),
    });
  }

  handleActionChange(item) {
    const selectedAction = this.actionList.find(
      (ele) => ele.emailTemplatingActionId === item?.value
    );

    if (!selectedAction) {
      this.showTemplateEditor = false;
      this.statusList = [];
      this.templateForm.get("status").setValue("");
      return;
    }
    if (selectedAction?.actionStatuses.length) {
      this.templateForm.get('status')?.setValidators([Validators.required])
      this.statusList = selectedAction?.actionStatuses;
      this.showTemplateEditor = false;
    } else {
      this.statusList = [];
      this.templateForm.get('status')?.removeValidators(Validators.required);
      this.templateForm.get("status").setValue("");
      this.showTemplateEditor = true;
    }
  }

  handleStatusChange(item) {
    const selectedStatus = this.statusList.find(
      (ele) => ele.emailTemplateActionStatusId === item?.value
    );

    if (!selectedStatus) {
      this.showTemplateEditor = false;
      return;
    }

    this.showTemplateEditor = true;
  }

  getEmailTemplate() {
    this.apiService
      .get(
        endpoints.settings.globalEmailTemplate.getEmailTemplate(this.templateId)
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: HttpResponse<any>) => {
        if (res?.body) {
          const emailTemplateDetails = res?.body?.emailTemplateDetails;

          this.templateForm
            .get("action")
            .setValue(
              emailTemplateDetails?.templateAction?.emailTemplatingActionId ||
                ""
            );

          if (emailTemplateDetails?.claimStatus) {
            this.statusList =
              emailTemplateDetails?.templateAction?.actionStatuses;
            this.templateForm
              .get("status")
              .setValue(
                emailTemplateDetails?.claimStatus?.emailTemplateActionStatusId
              );
          }
          this.showTemplateEditor = true;
          this.templateForm
            .get("templateContent")
            .setValue(emailTemplateDetails?.templateContent);
        }
      });
  }

  getAllEmailTemplateAction() {
    this.apiService
      .get(endpoints.settings.globalEmailTemplate.getAllEmailTemplateAction)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: HttpResponse<any>) => {
        if (res.body) {
          this.actionList = res.body?.data;
        }
      });
  }

  addEdit() {
    if(this.templateForm.invalid){
      this.templateForm.markAllAsTouched();
      return;
    }

    if (this.templateId) {
      this.createUpdatePayload();
    } else {
      this.createAddPayload();
    }
  }

  createUpdatePayload() {
    const payload = {
      templateActionId: this.templateForm.get("action").value,
      claimStatusId: this.templateForm.get("status").value,
      templateContent: this.templateForm.get("templateContent").value,
    };

    this.apiService
      .patch(
        endpoints.settings.globalEmailTemplate.updateEmailTemplate(
          this.templateId
        ),
        payload
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res: HttpResponse<any>) => {
          this.apiService.alert(res.body.message, ErrorType.Success);
          this.redirectToGlobalEmailTemplate();
        },
        (err) => {
          this.apiService.alert(err.message, ErrorType.Error);
        }
      );
  }

  createAddPayload() {
    const payload = {
      templateActionId: this.templateForm.get("action").value,
      claimStatusId: this.templateForm.get("status").value,
      templateContent: this.templateForm.get("templateContent").value,
    };

    this.apiService
      .post(endpoints.settings.globalEmailTemplate.createEmailTemplate, payload)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res: HttpResponse<any>) => {
          this.apiService.alert(res.body.message, ErrorType.Success);
          this.redirectToGlobalEmailTemplate();
        },
        (err) => {
          this.apiService.alert(err.message, ErrorType.Error);
        }
      );
  }

  redirectToGlobalEmailTemplate() {
    this.router.navigateByUrl(`/settings/global-email-template`);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
